import { collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category:
    | 'arts-entertainment'
    | 'blogs-updates'
    | 'business'
    | 'deals-offers'
    | 'ecommerce'
    | 'events'
    | 'festive-season'
    | 'health-wellness'
    | 'newsletter'
    | 'onboarding'
    | 'professional-services'
    | 'promotional'
    | 'inspirational'
    | 'basic'
    | 'yours';
  tags: string[];
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  variables: string[]; // List of merge tags used in the template
  previewImage?: string; // URL to preview image
  description?: string;
  // Marketplace additions
  isPaid?: boolean;
  price?: number;
  // Editor design JSON for email builder
  designJson?: unknown;
  displayOrder?: number;
  featuredOnDiscoverPage?: boolean;
  discoverPageOrder?: number;
}

export interface TemplateVariable {
  name: string;
  description: string;
  defaultValue?: string;
  required: boolean;
  type: 'text' | 'number' | 'date' | 'url' | 'email';
}

// Default template variables available in all templates
export const DEFAULT_TEMPLATE_VARIABLES: TemplateVariable[] = [
  { name: 'first_name', description: 'Recipient\'s first name', type: 'text', required: false },
  { name: 'last_name', description: 'Recipient\'s last name', type: 'text', required: false },
  { name: 'email', description: 'Recipient\'s email address', type: 'email', required: true },
  { name: 'company_name', description: 'Recipient\'s company name', type: 'text', required: false },
  { name: 'unsubscribe_url', description: 'Unsubscribe link', type: 'url', required: true },
  { name: 'website_url', description: 'Your website URL', type: 'url', required: false },
  { name: 'sender_name', description: 'Sender\'s name', type: 'text', required: false },
  { name: 'sender_email', description: 'Sender\'s email', type: 'email', required: false },
];

const isFirestoreTimestamp = (value: any): value is Timestamp =>
  Boolean(value && typeof value.toDate === 'function');

const getDateFromValue = (value: any): Date => {
  if (!value) {
    return new Date(0);
  }
  if (value instanceof Date) {
    return value;
  }
  if (isFirestoreTimestamp(value)) {
    try {
      return value.toDate();
    } catch (err) {
      console.warn('Failed to convert Firestore timestamp to Date', err);
      return new Date(0);
    }
  }
  const parsed = new Date(value as any);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

const getTimeValue = (value: any): number => getDateFromValue(value).getTime();

export const compareTemplateDisplayOrder = (a: EmailTemplate, b: EmailTemplate): number => {
  const aOrder = typeof a.displayOrder === 'number' ? a.displayOrder : null;
  const bOrder = typeof b.displayOrder === 'number' ? b.displayOrder : null;

  if (aOrder !== null && bOrder !== null) {
    if (aOrder === bOrder) {
      return 0;
    }
    return aOrder - bOrder;
  }
  if (aOrder !== null) {
    return -1;
  }
  if (bOrder !== null) {
    return 1;
  }
  return 0;
};

export const sortTemplatesByDisplayOrder = (templates: EmailTemplate[]): EmailTemplate[] => {
  return [...templates].sort((a, b) => {
    const orderComparison = compareTemplateDisplayOrder(a, b);
    if (orderComparison !== 0) {
      return orderComparison;
    }

    const aUpdated = getTimeValue(a.updatedAt || a.createdAt || (a as any).uploadedAt);
    const bUpdated = getTimeValue(b.updatedAt || b.createdAt || (b as any).uploadedAt);
    return bUpdated - aUpdated;
  });
};

// Create a new email template
export const createEmailTemplate = async (template: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const templateData = {
      ...template,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'email_templates'), templateData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating email template:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      template: template
    });
    throw new Error(`Failed to create email template: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Get all email templates
export const getEmailTemplates = async (userId: string, category?: string): Promise<EmailTemplate[]> => {
  try {
    let q = query(
      collection(db, 'email_templates'),
      where('createdBy', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    if (category) {
      q = query(q, where('category', '==', category));
    }

    const querySnapshot = await getDocs(q);
    const templates = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as EmailTemplate[];

    return sortTemplatesByDisplayOrder(templates);
  } catch (error) {
    console.error('Error getting email templates:', error);
    throw new Error('Failed to get email templates');
  }
};

// Public marketplace templates (free + premium published by admin)
export const getMarketplaceTemplates = async (opts?: { isPaid?: boolean; limit?: number }): Promise<EmailTemplate[]> => {
  try {
    // Query for system/admin templates by createdBy field
    const createdByQuery = query(
      collection(db, 'email_templates'),
      where('createdBy', 'in', ['system', 'admin'])
    );

    const createdBySnapshot = await getDocs(createdByQuery);
    
    const mergedById = new Map<string, EmailTemplate>();
    createdBySnapshot.docs.forEach((docSnapshot) => {
      mergedById.set(docSnapshot.id, { id: docSnapshot.id, ...docSnapshot.data() } as EmailTemplate);
    });

    // Try to get templates with uploadedBy field (legacy), but don't fail if it errors
    try {
      const uploadedByQuery = query(
        collection(db, 'email_templates'),
        where('uploadedBy', 'in', ['system', 'admin'])
      );
      const uploadedBySnapshot = await getDocs(uploadedByQuery);
      uploadedBySnapshot.docs.forEach((docSnapshot) => {
        mergedById.set(docSnapshot.id, { id: docSnapshot.id, ...docSnapshot.data() } as EmailTemplate);
      });
    } catch (uploadedByError) {
      // Ignore uploadedBy query errors - this field is legacy and may not exist
      console.log('Note: uploadedBy query skipped (field may not exist)');
    }

    let list = Array.from(mergedById.values());
    // Only active/published for marketplace view; treat undefined as active for backwards compatibility
    list = list.filter(t => t.isActive !== false);
    if (typeof opts?.isPaid === 'boolean') {
      list = list.filter(t => (t.isPaid ?? false) === opts.isPaid);
    }
    list = sortTemplatesByDisplayOrder(list);
    if (opts?.limit && opts.limit > 0) {
      list = list.slice(0, opts.limit);
    }
    return list;
  } catch (error) {
    console.error('Error getting marketplace templates:', error);
    throw new Error('Failed to get marketplace templates');
  }
};

// Create a user draft from a base template design
export const createUserTemplateFromBase = async (params: {
  userId: string;
  baseTemplateId: string;
  name: string;
  htmlContent: string;
  subject: string;
  variables: string[];
}): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'user_templates'), {
      ...params,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating user template from base:', error);
    throw new Error('Failed to create user template');
  }
};

// Get a single email template
export const getEmailTemplate = async (templateId: string): Promise<EmailTemplate | null> => {
  try {
    const docRef = doc(db, 'email_templates', templateId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as EmailTemplate;
    }
    return null;
  } catch (error) {
    console.error('Error getting email template:', error);
    throw new Error('Failed to get email template');
  }
};

// Update an email template
export const updateEmailTemplate = async (templateId: string, updates: Partial<EmailTemplate>): Promise<void> => {
  try {
    const docRef = doc(db, 'email_templates', templateId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating email template:', error);
    throw new Error('Failed to update email template');
  }
};

// Delete an email template
export const deleteEmailTemplate = async (templateId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'email_templates', templateId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting email template:', error);
    throw new Error('Failed to delete email template');
  }
};

// Process template with variables
export const processTemplate = (template: EmailTemplate, variables: { [key: string]: string }): {
  subject: string;
  htmlContent: string;
  textContent?: string;
} => {
  let processedSubject = template.subject;
  let processedHtml = template.htmlContent;
  let processedText = template.textContent;

  // Replace variables in subject and content
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    processedSubject = processedSubject.replace(regex, value || '');
    processedHtml = processedHtml.replace(regex, value || '');
    if (processedText) {
      processedText = processedText.replace(regex, value || '');
    }
  });

  return {
    subject: processedSubject,
    htmlContent: processedHtml,
    textContent: processedText,
  };
};

// Extract variables from template content
export const extractTemplateVariables = (content: string): string[] => {
  const variableRegex = /{{(\w+)}}/g;
  const variables = new Set<string>();
  let match;

  while ((match = variableRegex.exec(content)) !== null) {
    variables.add(match[1]);
  }

  return Array.from(variables);
};

// Create default templates
export const createDefaultTemplates = async (userId: string): Promise<void> => {
  const defaultTemplates = [
    {
      name: 'Welcome Email',
      subject: 'Welcome to {{company_name}}!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Welcome</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #2c3e50;">Welcome {{first_name}}!</h1>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
            <p>Thank you for joining {{company_name}}! We're excited to have you on board.</p>
            <p>Here's what you can expect from us:</p>
            <ul>
              <li>Regular updates about our products and services</li>
              <li>Exclusive offers and promotions</li>
              <li>Tips and insights to help you succeed</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{website_url}}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">Visit Our Website</a>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>If you no longer wish to receive these emails, you can <a href="{{unsubscribe_url}}">unsubscribe here</a>.</p>
          </div>
        </body>
        </html>
      `,
      textContent: `Welcome {{first_name}}!\n\nThank you for joining {{company_name}}! We're excited to have you on board.\n\nVisit our website: {{website_url}}\n\nIf you no longer wish to receive these emails, you can unsubscribe here: {{unsubscribe_url}}`,
      category: 'onboarding' as const,
      tags: ['welcome', 'onboarding'],
      isActive: true,
      createdBy: userId,
      variables: ['first_name', 'company_name', 'website_url', 'unsubscribe_url'],
      description: 'A warm welcome email for new subscribers',
    },
    {
      name: 'Newsletter Template',
      subject: '{{company_name}} Newsletter - {{date}}',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Newsletter</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #2c3e50;">{{company_name}} Newsletter</h1>
            <p style="color: #666;">{{date}}</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2>Featured Article</h2>
            <p>This is where your main newsletter content would go. You can include articles, updates, and other relevant information for your subscribers.</p>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3>Quick Updates</h3>
            <ul>
              <li>Update 1</li>
              <li>Update 2</li>
              <li>Update 3</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{website_url}}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">Read More</a>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>If you no longer wish to receive these emails, you can <a href="{{unsubscribe_url}}">unsubscribe here</a>.</p>
          </div>
        </body>
        </html>
      `,
      textContent: `{{company_name}} Newsletter - {{date}}\n\nFeatured Article\nThis is where your main newsletter content would go.\n\nQuick Updates\n- Update 1\n- Update 2\n- Update 3\n\nRead more: {{website_url}}\n\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'newsletter' as const,
      tags: ['newsletter', 'monthly'],
      isActive: true,
      createdBy: userId,
      variables: ['company_name', 'date', 'website_url', 'unsubscribe_url'],
      description: 'A clean newsletter template for regular updates',
    },
  ];

  try {
    for (const template of defaultTemplates) {
      await createEmailTemplate(template);
    }
  } catch (error) {
    console.error('Error creating default templates:', error);
    throw new Error('Failed to create default templates');
  }
};

// Import a small set of Postmark-style templates (MIT-inspired) as free templates
export const importPostmarkTemplates = async (userId: string): Promise<void> => {
  const templates: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>[] = [
    {
      name: 'Postmark Welcome',
      subject: 'Welcome to {{company_name}}',
      htmlContent: `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>Welcome</title></head><body style="background:#F2F4F6;margin:0;padding:0"><table role="presentation" width="100%" style="background:#F2F4F6;padding:24px 0"><tr><td align="center"><table role="presentation" width="600" style="background:#FFFFFF;border:1px solid #EAEAEC;border-radius:4px;overflow:hidden"><tr><td style="padding:24px 24px 0 24px;text-align:center"><h1 style="margin:0 0 12px 0;color:#2F3133;font-family:Arial,Helvetica,sans-serif;font-size:24px">Welcome, {{first_name}}!</h1><p style="margin:0 0 16px 0;color:#74787E;font-family:Arial,Helvetica,sans-serif;font-size:14px">Thanks for joining {{company_name}}. We’re thrilled to have you.</p><div style="margin:24px 0"><a href="{{website_url}}" style="background:#6C63FF;color:#fff;text-decoration:none;padding:12px 20px;border-radius:4px;display:inline-block;font-family:Arial,Helvetica,sans-serif;font-size:14px">Get Started</a></div></td></tr><tr><td style="padding:0 24px 24px 24px;color:#74787E;font-family:Arial,Helvetica,sans-serif;font-size:12px;text-align:center">If you didn’t create this account, please ignore this email. <br/> <a href="{{unsubscribe_url}}" style="color:#6C63FF">Unsubscribe</a></td></tr></table></td></tr></table></body></html>`,
      textContent: `Welcome, {{first_name}}! Visit {{website_url}}. Unsubscribe: {{unsubscribe_url}}`,
      category: 'onboarding',
      tags: ['postmark', 'welcome'],
      isActive: true,
      createdBy: userId,
      variables: ['first_name', 'company_name', 'website_url', 'unsubscribe_url'],
      description: 'Postmark-style welcome email',
      isPaid: false,
    },
    {
      name: 'Postmark Password Reset',
      subject: 'Reset your password',
      htmlContent: `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>Password Reset</title></head><body style="background:#F2F4F6;margin:0;padding:0"><table role="presentation" width="100%" style="background:#F2F4F6;padding:24px 0"><tr><td align="center"><table role="presentation" width="600" style="background:#FFFFFF;border:1px solid #EAEAEC;border-radius:4px"><tr><td style="padding:24px;color:#2F3133;font-family:Arial,Helvetica,sans-serif"><h1 style="margin:0 0 12px 0;font-size:22px">Password Reset</h1><p style="margin:0 0 16px 0;color:#74787E;font-size:14px">Tap the button below to reset your password. This link is valid for 60 minutes.</p><div style="margin:24px 0;text-align:center"><a href="{{reset_url}}" style="background:#6C63FF;color:#fff;text-decoration:none;padding:12px 20px;border-radius:4px;display:inline-block;font-size:14px">Reset password</a></div><p style="margin:0;color:#9EA3A8;font-size:12px">If you didn’t request a reset, you can safely ignore this email.</p></td></tr></table></td></tr></table></body></html>`,
      textContent: `Reset your password: {{reset_url}}`,
      category: 'business',
      tags: ['postmark', 'security'],
      isActive: true,
      createdBy: userId,
      variables: ['reset_url'],
      description: 'Postmark-style password reset email',
      isPaid: false,
    },
    {
      name: 'Postmark Receipt',
      subject: 'Payment receipt for {{company_name}}',
      htmlContent: `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>Receipt</title></head><body style="background:#F2F4F6;margin:0;padding:0"><table role="presentation" width="100%" style="background:#F2F4F6;padding:24px 0"><tr><td align="center"><table role="presentation" width="600" style="background:#FFFFFF;border:1px solid #EAEAEC;border-radius:4px"><tr><td style="padding:24px;color:#2F3133;font-family:Arial,Helvetica,sans-serif"><h1 style="margin:0 0 12px 0;font-size:22px">Thanks for your purchase</h1><p style="margin:0 0 16px 0;color:#74787E;font-size:14px">Here’s your receipt. Keep it for your records.</p><table role="presentation" width="100%" style="margin-top:12px;border-collapse:collapse"><tr><td style="padding:8px 0;color:#2F3133;font-size:14px">Amount</td><td style="padding:8px 0;color:#2F3133;font-size:14px;text-align:right">{{amount}}</td></tr><tr><td style="padding:8px 0;color:#2F3133;font-size:14px">Date</td><td style="padding:8px 0;color:#2F3133;font-size:14px;text-align:right">{{date}}</td></tr></table><div style="margin-top:24px;text-align:center"><a href="{{invoice_url}}" style="color:#6C63FF;font-size:14px">View invoice</a></div></td></tr></table></td></tr></table></body></html>`,
      textContent: `Amount: {{amount}}\nDate: {{date}}\nInvoice: {{invoice_url}}`,
      category: 'business',
      tags: ['postmark', 'receipt'],
      isActive: true,
      createdBy: userId,
      variables: ['amount', 'date', 'invoice_url'],
      description: 'Postmark-style payment receipt',
      isPaid: false,
    },
  ];

  try {
    for (const t of templates) {
      await createEmailTemplate(t);
    }
  } catch (error) {
    console.error('Error importing Postmark templates:', error);
    throw new Error('Failed to import Postmark templates');
  }
};

// Remove Postmark templates created for a user
export const deletePostmarkTemplatesForUser = async (userId: string): Promise<number> => {
  try {
    const ids = new Set<string>();
    // By tag
    const qByTag = query(
      collection(db, 'email_templates'),
      where('createdBy', '==', userId),
      where('tags', 'array-contains', 'postmark')
    );
    const byTagSnap = await getDocs(qByTag);
    byTagSnap.forEach(d => ids.add(d.id));

    // By known names (in case tags are missing)
    const knownNames = ['Postmark Welcome', 'Postmark Password Reset', 'Postmark Receipt'];
    // Firestore 'in' supports up to 10 values
    const qByName = query(
      collection(db, 'email_templates'),
      where('createdBy', '==', userId),
      where('name', 'in', knownNames as any)
    );
    try {
      const byNameSnap = await getDocs(qByName);
      byNameSnap.forEach(d => ids.add(d.id));
    } catch {
      // Ignore if index not available
    }

    // Delete all collected ids
    const idList = Array.from(ids);
    await Promise.all(idList.map(id => deleteDoc(doc(db, 'email_templates', id))));
    return idList.length;
  } catch (error) {
    console.error('Error deleting Postmark templates:', error);
    throw new Error('Failed to delete Postmark templates');
  }
};

// Create comprehensive marketplace templates
export const createMarketplaceTemplates = async (): Promise<void> => {
  const marketplaceTemplates: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>[] = [
    // E-commerce Templates
    {
      name: 'Product Launch Announcement',
      subject: 'Introducing {{product_name}} - Now Available!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Product Launch</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">{{product_name}} is Here!</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">The future of {{product_category}} is now available</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">What's New?</h2>
            <p>{{product_description}}</p>
            <ul style="color: #555;">
              <li>Feature 1: {{feature_1}}</li>
              <li>Feature 2: {{feature_2}}</li>
              <li>Feature 3: {{feature_3}}</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{product_url}}" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Shop Now - {{price}}</a>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #2c3e50; margin-top: 0;">Limited Time Offer</h3>
            <p>Get {{discount_percentage}}% off your first order with code: <strong>{{discount_code}}</strong></p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>If you no longer wish to receive these emails, you can <a href="{{unsubscribe_url}}">unsubscribe here</a>.</p>
          </div>
        </body>
        </html>
      `,
      textContent: `{{product_name}} is Here!\n\n{{product_description}}\n\nShop Now: {{product_url}}\n\nLimited Time: {{discount_percentage}}% off with code {{discount_code}}\n\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'ecommerce' as const,
      tags: ['ecommerce', 'product-launch', 'announcement'],
      isActive: true,
      createdBy: 'system',
      variables: ['product_name', 'product_category', 'product_description', 'feature_1', 'feature_2', 'feature_3', 'product_url', 'price', 'discount_percentage', 'discount_code', 'unsubscribe_url'],
      description: 'Eye-catching product launch announcement with gradient design',
      isPaid: false,
    },
    
    // Marketing Templates
    {
      name: 'Social Media Campaign',
      subject: 'Join the Conversation - #{{hashtag}}',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Social Campaign</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #1da1f2; margin: 0; font-size: 28px;">#{{hashtag}}</h1>
            <p style="color: #666; font-size: 16px;">Join thousands of people sharing their stories</p>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 25px; border-radius: 10px; margin-bottom: 25px;">
            <h2 style="color: #2c3e50; margin-top: 0;">How to Participate</h2>
            <ol style="color: #555; padding-left: 20px;">
              <li>Share your {{campaign_theme}} story</li>
              <li>Use the hashtag #{{hashtag}}</li>
              <li>Tag us @{{company_handle}}</li>
              <li>Encourage others to join!</li>
            </ol>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{campaign_url}}" style="background-color: #1da1f2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 20px; display: inline-block; font-weight: bold;">Start Sharing</a>
          </div>
          
          <div style="background-color: #e8f4fd; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #1da1f2; margin-top: 0;">Prize Alert!</h3>
            <p style="margin: 0;">The best story wins {{prize_description}}!</p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Follow us: <a href="{{social_links}}">@{{company_handle}}</a> | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `Join #{{hashtag}} Campaign\n\nShare your {{campaign_theme}} story and use #{{hashtag}}\n\nPrize: {{prize_description}}\n\nStart: {{campaign_url}}\n\nFollow: @{{company_handle}}\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'promotional' as const,
      tags: ['marketing', 'social-media', 'campaign', 'hashtag'],
      isActive: true,
      createdBy: 'system',
      variables: ['hashtag', 'campaign_theme', 'company_handle', 'campaign_url', 'prize_description', 'social_links', 'unsubscribe_url'],
      description: 'Engaging social media campaign template with hashtag focus',
      isPaid: false,
    },
    
    // Educational Templates
    {
      name: 'Course Launch Email',
      subject: 'New Course: {{course_title}} - Enroll Now!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Course Launch</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">{{course_title}}</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">Master {{subject_area}} in {{course_duration}}</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">What You'll Learn</h2>
            <ul style="color: #555;">
              <li>{{learning_outcome_1}}</li>
              <li>{{learning_outcome_2}}</li>
              <li>{{learning_outcome_3}}</li>
              <li>{{learning_outcome_4}}</li>
            </ul>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #2c3e50; margin-top: 0;">Course Details</h3>
            <p><strong>Duration:</strong> {{course_duration}}</p>
            <p><strong>Level:</strong> {{difficulty_level}}</p>
            <p><strong>Format:</strong> {{course_format}}</p>
            <p><strong>Instructor:</strong> {{instructor_name}}</p>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{enrollment_url}}" style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Enroll Now - {{course_price}}</a>
          </div>
          
          <div style="background-color: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #28a745; margin-top: 0;">Early Bird Special</h3>
            <p style="margin: 0;">Get {{discount_percentage}}% off if you enroll before {{early_bird_deadline}}!</p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Questions? Contact us at {{support_email}} | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `New Course: {{course_title}}\n\nMaster {{subject_area}} in {{course_duration}}\n\nWhat you'll learn:\n- {{learning_outcome_1}}\n- {{learning_outcome_2}}\n- {{learning_outcome_3}}\n- {{learning_outcome_4}}\n\nEnroll: {{enrollment_url}} - {{course_price}}\n\nEarly Bird: {{discount_percentage}}% off until {{early_bird_deadline}}\n\nSupport: {{support_email}}\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'blogs-updates' as const,
      tags: ['educational', 'course', 'learning', 'online-education'],
      isActive: true,
      createdBy: 'system',
      variables: ['course_title', 'subject_area', 'course_duration', 'learning_outcome_1', 'learning_outcome_2', 'learning_outcome_3', 'learning_outcome_4', 'difficulty_level', 'course_format', 'instructor_name', 'enrollment_url', 'course_price', 'discount_percentage', 'early_bird_deadline', 'support_email', 'unsubscribe_url'],
      description: 'Professional course launch email with learning outcomes',
      isPaid: false,
    },
    
    // Seasonal Templates
    {
      name: 'Holiday Sale Announcement',
      subject: '{{holiday_name}} Sale - Up to {{discount_percentage}}% Off!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Holiday Sale</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 32px;">🎉 {{holiday_name}} Sale! 🎉</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 18px;">Up to {{discount_percentage}}% off everything!</p>
          </div>
          
          <div style="background-color: #fff3cd; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ffc107;">
            <h3 style="color: #856404; margin-top: 0;">⏰ Limited Time Offer</h3>
            <p style="color: #856404; margin: 0;">This {{holiday_name}} sale ends on {{sale_end_date}}. Don't miss out!</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">Featured Deals</h2>
            <div style="display: flex; justify-content: space-between; margin: 20px 0;">
              <div style="flex: 1; margin: 0 10px; text-align: center; background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
                <h4 style="margin: 0 0 10px 0; color: #2c3e50;">{{product_1_name}}</h4>
                <p style="margin: 0; color: #e74c3c; font-weight: bold;">{{product_1_discount}}</p>
              </div>
              <div style="flex: 1; margin: 0 10px; text-align: center; background-color: #f8f9fa; padding: 15px; border-radius: 8px;">
                <h4 style="margin: 0 0 10px 0; color: #2c3e50;">{{product_2_name}}</h4>
                <p style="margin: 0; color: #e74c3c; font-weight: bold;">{{product_2_discount}}</p>
              </div>
            </div>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{sale_url}}" style="background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold; font-size: 16px;">Shop {{holiday_name}} Sale</a>
          </div>
          
          <div style="background-color: #d4edda; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #155724; margin-top: 0;">🎁 Bonus Offer</h3>
            <p style="color: #155724; margin: 0;">Spend over {{free_shipping_threshold}} and get free shipping + {{bonus_gift}}!</p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Happy {{holiday_name}} from {{company_name}}! | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `🎉 {{holiday_name}} Sale - Up to {{discount_percentage}}% Off! 🎉\n\nLimited time offer until {{sale_end_date}}\n\nFeatured Deals:\n- {{product_1_name}}: {{product_1_discount}}\n- {{product_2_name}}: {{product_2_discount}}\n\nShop Now: {{sale_url}}\n\nBonus: Free shipping over {{free_shipping_threshold}} + {{bonus_gift}}\n\nHappy {{holiday_name}} from {{company_name}}!\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'festive-season' as const,
      tags: ['seasonal', 'holiday', 'sale', 'discount', 'limited-time'],
      isActive: true,
      createdBy: 'system',
      variables: ['holiday_name', 'discount_percentage', 'sale_end_date', 'product_1_name', 'product_1_discount', 'product_2_name', 'product_2_discount', 'sale_url', 'free_shipping_threshold', 'bonus_gift', 'company_name', 'unsubscribe_url'],
      description: 'Festive holiday sale email with emojis and urgency',
      isPaid: false,
    },
    
    // Event Templates
    {
      name: 'Event Invitation',
      subject: 'You\'re Invited: {{event_name}}',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Event Invitation</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">{{event_name}}</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">{{event_tagline}}</p>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 25px; border-radius: 10px; margin-bottom: 25px;">
            <h2 style="color: #2c3e50; margin-top: 0;">Event Details</h2>
            <div style="margin: 15px 0;">
              <strong>📅 Date:</strong> {{event_date}}<br>
              <strong>🕐 Time:</strong> {{event_time}}<br>
              <strong>📍 Location:</strong> {{event_location}}<br>
              <strong>💰 Price:</strong> {{event_price}}
            </div>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">About This Event</h2>
            <p>{{event_description}}</p>
            
            <h3 style="color: #2c3e50;">What to Expect</h3>
            <ul style="color: #555;">
              <li>{{event_highlight_1}}</li>
              <li>{{event_highlight_2}}</li>
              <li>{{event_highlight_3}}</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{rsvp_url}}" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">RSVP Now</a>
          </div>
          
          <div style="background-color: #e8f4fd; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #1da1f2; margin-top: 0;">📱 Share the Event</h3>
            <p style="margin: 0;">Help us spread the word! Share this event with your network.</p>
            <div style="text-align: center; margin-top: 15px;">
              <a href="{{share_url}}" style="color: #1da1f2; text-decoration: none; font-weight: bold;">Share on Social Media</a>
            </div>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Questions? Contact {{organizer_name}} at {{organizer_email}} | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `You're Invited: {{event_name}}\n\n{{event_tagline}}\n\nEvent Details:\n📅 {{event_date}} at {{event_time}}\n📍 {{event_location}}\n💰 {{event_price}}\n\nAbout: {{event_description}}\n\nHighlights:\n- {{event_highlight_1}}\n- {{event_highlight_2}}\n- {{event_highlight_3}}\n\nRSVP: {{rsvp_url}}\nShare: {{share_url}}\n\nContact: {{organizer_name}} ({{organizer_email}})\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'events' as const,
      tags: ['event', 'invitation', 'rsvp', 'networking', 'conference'],
      isActive: true,
      createdBy: 'system',
      variables: ['event_name', 'event_tagline', 'event_date', 'event_time', 'event_location', 'event_price', 'event_description', 'event_highlight_1', 'event_highlight_2', 'event_highlight_3', 'rsvp_url', 'share_url', 'organizer_name', 'organizer_email', 'unsubscribe_url'],
      description: 'Professional event invitation with all details and RSVP',
      isPaid: false,
    },
    
    // Follow-up Templates
    {
      name: 'Post-Purchase Follow-up',
      subject: 'How was your {{product_name}} experience?',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Follow-up</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #2c3e50; margin: 0; font-size: 28px;">Hi {{first_name}}!</h1>
            <p style="color: #666; font-size: 16px;">We hope you're enjoying your {{product_name}}</p>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 25px; border-radius: 10px; margin-bottom: 25px;">
            <h2 style="color: #2c3e50; margin-top: 0;">How was your experience?</h2>
            <p>We'd love to hear about your experience with {{product_name}}. Your feedback helps us improve and helps other customers make informed decisions.</p>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{review_url}}" style="background-color: #28a745; color: white; padding: 12px 24px; text-decoration: none; border-radius: 20px; display: inline-block; font-weight: bold; margin: 5px;">⭐ Leave a Review</a>
            <a href="{{feedback_url}}" style="background-color: #17a2b8; color: white; padding: 12px 24px; text-decoration: none; border-radius: 20px; display: inline-block; font-weight: bold; margin: 5px;">💬 Send Feedback</a>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">Need Help?</h2>
            <p>If you have any questions or need assistance with {{product_name}}, we're here to help!</p>
            <ul style="color: #555;">
              <li><a href="{{help_center_url}}" style="color: #007bff;">Visit our Help Center</a></li>
              <li><a href="{{support_email}}" style="color: #007bff;">Contact Support</a></li>
              <li><a href="{{faq_url}}" style="color: #007bff;">Check our FAQ</a></li>
            </ul>
          </div>
          
          <div style="background-color: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #28a745; margin-top: 0;">🎁 Special Offer</h3>
            <p style="color: #155724; margin: 0;">As a valued customer, enjoy {{discount_percentage}}% off your next purchase with code: <strong>{{loyalty_code}}</strong></p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Thank you for choosing {{company_name}}! | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `Hi {{first_name}}!\n\nWe hope you're enjoying your {{product_name}}.\n\nHow was your experience?\n- Leave a review: {{review_url}}\n- Send feedback: {{feedback_url}}\n\nNeed help?\n- Help Center: {{help_center_url}}\n- Support: {{support_email}}\n- FAQ: {{faq_url}}\n\nSpecial offer: {{discount_percentage}}% off with code {{loyalty_code}}\n\nThank you from {{company_name}}!\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'business' as const,
      tags: ['follow-up', 'post-purchase', 'feedback', 'review', 'customer-service'],
      isActive: true,
      createdBy: 'system',
      variables: ['first_name', 'product_name', 'review_url', 'feedback_url', 'help_center_url', 'support_email', 'faq_url', 'discount_percentage', 'loyalty_code', 'company_name', 'unsubscribe_url'],
      description: 'Warm post-purchase follow-up with review request and support',
      isPaid: false,
    },
    
    // Thank You Templates
    {
      name: 'Thank You for Subscription',
      subject: 'Thank you for subscribing to {{newsletter_name}}!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Thank You</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Thank You, {{first_name}}!</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">Welcome to {{newsletter_name}}</p>
          </div>
          
          <div style="background-color: #f8f9fa; padding: 25px; border-radius: 10px; margin-bottom: 25px;">
            <h2 style="color: #2c3e50; margin-top: 0;">What happens next?</h2>
            <ol style="color: #555; padding-left: 20px;">
              <li>You'll receive our weekly newsletter every {{newsletter_frequency}}</li>
              <li>Get exclusive content and early access to new features</li>
              <li>Join our community of {{subscriber_count}}+ subscribers</li>
            </ol>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">What to expect from {{newsletter_name}}</h2>
            <ul style="color: #555;">
              <li>{{content_type_1}}</li>
              <li>{{content_type_2}}</li>
              <li>{{content_type_3}}</li>
              <li>{{content_type_4}}</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{welcome_series_url}}" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Start Your Journey</a>
          </div>
          
          <div style="background-color: #e8f4fd; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #1da1f2; margin-top: 0;">📱 Connect with us</h3>
            <p style="margin: 0;">Follow us on social media for daily updates and behind-the-scenes content!</p>
            <div style="text-align: center; margin-top: 15px;">
              <a href="{{social_links}}" style="color: #1da1f2; text-decoration: none; font-weight: bold;">Follow Us</a>
            </div>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Welcome to the {{company_name}} family! | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `Thank You, {{first_name}}!\n\nWelcome to {{newsletter_name}}!\n\nWhat happens next:\n1. Weekly newsletter every {{newsletter_frequency}}\n2. Exclusive content and early access\n3. Join {{subscriber_count}}+ subscribers\n\nWhat to expect:\n- {{content_type_1}}\n- {{content_type_2}}\n- {{content_type_3}}\n- {{content_type_4}}\n\nStart: {{welcome_series_url}}\nFollow: {{social_links}}\n\nWelcome to {{company_name}}!\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'promotional' as const,
      tags: ['thank-you', 'subscription', 'welcome', 'newsletter', 'onboarding'],
      isActive: true,
      createdBy: 'system',
      variables: ['first_name', 'newsletter_name', 'newsletter_frequency', 'subscriber_count', 'content_type_1', 'content_type_2', 'content_type_3', 'content_type_4', 'welcome_series_url', 'social_links', 'company_name', 'unsubscribe_url'],
      description: 'Warm thank you email for newsletter subscription',
      isPaid: false,
    },
    
    // Additional Template Example
    {
      name: 'Customer Onboarding Series',
      subject: 'Welcome to {{company_name}} - Let\'s Get Started!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Welcome Onboarding</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Welcome to {{company_name}}, {{first_name}}!</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">Let's get you started on your journey</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2c3e50;">Your Next Steps</h2>
            <ol style="color: #555; padding-left: 20px;">
              <li><strong>Complete your profile</strong> - Add your preferences</li>
              <li><strong>Explore features</strong> - Discover what we offer</li>
              <li><strong>Connect with us</strong> - We're here to help</li>
            </ol>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{onboarding_url}}" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Start Your Journey</a>
          </div>
          
          <div style="background-color: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #166534; margin-top: 0;">💡 Pro Tip</h3>
            <p style="color: #166534; margin: 0;">Check out our {{help_center_url}} for quick answers to common questions!</p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>Questions? Reply to this email or visit {{support_url}} | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `Welcome to {{company_name}}, {{first_name}}!\n\nYour Next Steps:\n1. Complete your profile - Add your preferences\n2. Explore features - Discover what we offer\n3. Connect with us - We're here to help\n\nStart: {{onboarding_url}}\n\nPro Tip: Check out {{help_center_url}} for quick answers!\n\nSupport: {{support_url}}\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'onboarding' as const,
      tags: ['onboarding', 'welcome', 'customer-success', 'getting-started'],
      isActive: true,
      createdBy: 'system',
      variables: ['first_name', 'company_name', 'onboarding_url', 'help_center_url', 'support_url', 'unsubscribe_url'],
      description: 'Comprehensive onboarding email with next steps and resources',
      isPaid: false,
    },
    
    // Real Estate Templates
    {
      name: 'Property Listing Announcement',
      subject: 'New Property Available: {{property_address}}',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Property Listing</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">🏠 New Property Available!</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">{{property_address}}</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #1e40af;">Property Details</h2>
            <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0;">
              <p><strong>Price:</strong> {{property_price}}</p>
              <p><strong>Bedrooms:</strong> {{bedrooms}} | <strong>Bathrooms:</strong> {{bathrooms}}</p>
              <p><strong>Square Feet:</strong> {{square_feet}}</p>
              <p><strong>Property Type:</strong> {{property_type}}</p>
            </div>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #1e40af;">Key Features</h2>
            <ul style="color: #555; padding-left: 20px;">
              <li>{{feature_1}}</li>
              <li>{{feature_2}}</li>
              <li>{{feature_3}}</li>
              <li>{{feature_4}}</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{property_url}}" style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">View Property Details</a>
          </div>
          
          <div style="background-color: #dbeafe; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #1e40af; margin-top: 0;">📞 Contact Information</h3>
            <p style="color: #1e40af; margin: 0;">Ready to schedule a viewing? Contact {{agent_name}} at {{agent_phone}} or {{agent_email}}</p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>This property listing is brought to you by {{company_name}} | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `🏠 New Property Available: {{property_address}}\n\nProperty Details:\nPrice: {{property_price}}\nBedrooms: {{bedrooms}} | Bathrooms: {{bathrooms}}\nSquare Feet: {{square_feet}}\nProperty Type: {{property_type}}\n\nKey Features:\n- {{feature_1}}\n- {{feature_2}}\n- {{feature_3}}\n- {{feature_4}}\n\nView Property: {{property_url}}\n\nContact: {{agent_name}} ({{agent_phone}} | {{agent_email}})\n\nBrought to you by {{company_name}}\nUnsubscribe: {{unsubscribe_url}}`,
      category: 'professional-services' as const,
      tags: ['real-estate', 'property', 'listing', 'announcement'],
      isActive: true,
      createdBy: 'system',
      variables: ['property_address', 'property_price', 'bedrooms', 'bathrooms', 'square_feet', 'property_type', 'feature_1', 'feature_2', 'feature_3', 'feature_4', 'property_url', 'agent_name', 'agent_phone', 'agent_email', 'company_name', 'unsubscribe_url'],
      description: 'Professional property listing announcement with key details',
      isPaid: false,
    },
    
    // Admin Professional Templates
    {
      name: 'Professional Newsletter (Admin)',
      subject: '{{company_name}} Newsletter - {{month_year}}',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Professional Newsletter</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">{{company_name}} Newsletter</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">{{month_year}}</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #1e40af;">What's New This Month</h2>
            <p style="color: #555; font-size: 16px;">{{newsletter_intro}}</p>
          </div>
          
          <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #1e40af; margin-top: 0;">Featured Article</h3>
            <h4 style="color: #333;">{{article_title}}</h4>
            <p style="color: #555;">{{article_summary}}</p>
            <a href="{{article_url}}" style="color: #3b82f6; text-decoration: none;">Read More →</a>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{cta_url}}" style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">{{cta_text}}</a>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>{{company_name}} | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `{{company_name}} Newsletter - {{month_year}}\n\nWhat's New This Month:\n{{newsletter_intro}}\n\nFeatured Article: {{article_title}}\n{{article_summary}}\nRead More: {{article_url}}\n\n{{cta_text}}: {{cta_url}}\n\n{{company_name}} | Unsubscribe: {{unsubscribe_url}}`,
      category: 'newsletter' as const,
      tags: ['newsletter', 'professional', 'business', 'monthly', 'admin'],
      isActive: true,
      createdBy: 'admin',
      variables: ['company_name', 'month_year', 'newsletter_intro', 'article_title', 'article_summary', 'article_url', 'cta_text', 'cta_url', 'unsubscribe_url'],
      description: 'Professional monthly newsletter template with featured articles and CTA',
      isPaid: false,
    },
    
    {
      name: 'Product Launch Announcement (Admin)',
      subject: '🚀 Introducing {{product_name}} - Now Available!',
      htmlContent: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Product Launch</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px; background: linear-gradient(135deg, #059669 0%, #10b981 100%); padding: 40px 20px; border-radius: 10px;">
            <h1 style="color: white; margin: 0; font-size: 28px;">🚀 Introducing {{product_name}}</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">Now Available in Our Store!</p>
          </div>
          
          <div style="margin-bottom: 30px;">
            <h2 style="color: #059669;">Why You'll Love {{product_name}}</h2>
            <p style="color: #555; font-size: 16px;">{{product_description}}</p>
          </div>
          
          <div style="background-color: #ecfdf5; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #059669; margin-top: 0;">Key Features</h3>
            <ul style="color: #555; padding-left: 20px;">
              <li>{{feature_1}}</li>
              <li>{{feature_2}}</li>
              <li>{{feature_3}}</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{product_url}}" style="background: linear-gradient(135deg, #059669 0%, #10b981 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; display: inline-block; font-weight: bold;">Shop Now - {{product_price}}</a>
          </div>
          
          <div style="background-color: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="color: #059669; margin-top: 0;">🎉 Special Launch Offer</h3>
            <p style="color: #059669; margin: 0;">{{special_offer}}</p>
          </div>
          
          <div style="text-align: center; font-size: 12px; color: #666; margin-top: 30px;">
            <p>{{company_name}} | <a href="{{unsubscribe_url}}">Unsubscribe</a></p>
          </div>
        </body>
        </html>
      `,
      textContent: `🚀 Introducing {{product_name}} - Now Available!\n\nWhy You'll Love {{product_name}}:\n{{product_description}}\n\nKey Features:\n- {{feature_1}}\n- {{feature_2}}\n- {{feature_3}}\n\nShop Now: {{product_url}} - {{product_price}}\n\n🎉 Special Launch Offer: {{special_offer}}\n\n{{company_name}} | Unsubscribe: {{unsubscribe_url}}`,
      category: 'ecommerce' as const,
      tags: ['product', 'launch', 'ecommerce', 'announcement', 'admin'],
      isActive: true,
      createdBy: 'admin',
      variables: ['product_name', 'product_description', 'feature_1', 'feature_2', 'feature_3', 'product_url', 'product_price', 'special_offer', 'company_name', 'unsubscribe_url'],
      description: 'Exciting product launch announcement with features and special offers',
      isPaid: false,
    }
  ];

  try {
    for (const template of marketplaceTemplates) {
      await createEmailTemplate(template);
    }
  } catch (error) {
    console.error('Error creating marketplace templates:', error);
    throw new Error('Failed to create marketplace templates');
  }
};

export default {
  createEmailTemplate,
  getEmailTemplates,
  getMarketplaceTemplates,
  getEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  processTemplate,
  extractTemplateVariables,
  createDefaultTemplates,
  DEFAULT_TEMPLATE_VARIABLES,
  importPostmarkTemplates,
  deletePostmarkTemplatesForUser,
  createUserTemplateFromBase,
  createMarketplaceTemplates,
};

